import { Heading, Subtitle } from '@/components/Typography';
import { Course, SectionData } from '@/types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QuickAccessSection from './QuickAccessSection';
import SectionDrawer from './SectionDrawer';

interface QuickAccessProps {
    course: Course;
}

interface SectionGroup {
    groupName: string | null;
    sections: SectionData[];
}

const QuickAccess: React.FC<QuickAccessProps> = ({ course }) => {
    const { t } = useTranslation();
    const [selectedSection, setSelectedSection] = useState<SectionData | null>(null);

    const quickAccessSections = course.sections.filter(
        (section) => !section.hide_from_quick_access
    );

    const visibleSections =
        quickAccessSections.length > 0 ? quickAccessSections : course.sections;

    const groupedSections = useMemo(() => {
        const groups: SectionGroup[] = [];
        const seenGroups = new Map<string | null, number>();

        visibleSections.forEach((section) => {
            const groupName = section.quick_access_group || null;
            const existingGroupIndex = seenGroups.get(groupName);

            if (existingGroupIndex !== undefined) {
                groups[existingGroupIndex].sections.push(section);
            } else {
                seenGroups.set(groupName, groups.length);
                groups.push({ groupName, sections: [section] });
            }
        });

        return groups;
    }, [visibleSections]);

    if (visibleSections.length === 0) {
        return null;
    }

    return (
        <>
            <div className="pb-8">
                <div className="mb-6 flex items-center gap-4">
                    <div className="h-px flex-1 bg-gray-400 dark:bg-neutral-500" />
                    <Heading as="h2" className="text-center text-2xl dark:text-white">
                        {t('course.modules', 'Módulos')}
                    </Heading>
                    <div className="h-px flex-1 bg-gray-400 dark:bg-neutral-500" />
                </div>

                <div className="space-y-6">
                    {groupedSections.map((group, groupIndex) => (
                        <div key={group.groupName || `ungrouped-${groupIndex}`}>
                            {group.groupName && (
                                <Subtitle as="h3" className="mb-3 text-sm text-gray-700 dark:text-neutral-300">
                                    {group.groupName}
                                </Subtitle>
                            )}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {group.sections.map((section) => (
                                    <QuickAccessSection
                                        key={section.id}
                                        section={section}
                                        onClick={() => setSelectedSection(section)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <SectionDrawer
                section={selectedSection}
                courseSlug={course.slug}
                onClose={() => setSelectedSection(null)}
            />
        </>
    );
};

export default QuickAccess;
